import { LogRecord } from "@fairfleet/geotab";

export type LatLng = [number, number];

export interface VisitTrack {
  /** The drive in, joined to the visit's first position. */
  before: LatLng[];
  inside: LatLng[];
  /** The drive out, starting at the visit's last position. */
  after: LatLng[];
}

/** How far the map shows the drive in and out around a visit. */
const MARGIN_MS = 10 * 60_000;

/** Positions to load for a visit's map: a little before the entry until a little after the exit (or now). */
export function visitMapWindow(entry: Date, exit: Date | null, now: Date): { from: Date; to: Date } {
  return {
    from: new Date(entry.getTime() - MARGIN_MS),
    to: exit ? new Date(exit.getTime() + MARGIN_MS) : now,
  };
}

/** Splits a vehicle's positions into before, during and after a visit; a running visit has no exit. */
export function visitTrack(records: LogRecord[], entry: Date, exit: Date | null): VisitTrack {
  const points = records
    .filter(record => Number.isFinite(record.latitude) && Number.isFinite(record.longitude))
    .map(record => ({ time: new Date(record.dateTime).getTime(), at: [record.latitude, record.longitude] as LatLng }))
    .sort((a, b) => a.time - b.time);
  const start = entry.getTime();
  const end = exit?.getTime() ?? Number.POSITIVE_INFINITY;

  const before = points.filter(p => p.time < start).map(p => p.at);
  const inside = points.filter(p => p.time >= start && p.time <= end).map(p => p.at);
  const after = points.filter(p => p.time > end).map(p => p.at);
  return {
    before: before.length > 0 && inside.length > 0 ? [...before, inside[0]] : before,
    inside,
    after: after.length > 0 && inside.length > 0 ? [inside[inside.length - 1], ...after] : after,
  };
}
